/**
 * WaitlistLayout - the frame for the waitlist pages (/ehr, /rate-check).
 *
 * The page opens with the homepage hero, a form where the CTA would be: the
 * same aurora glows, Constellation settings, H1 and subhead type, and the
 * InvestorStrip pinned to the bottom edge. The hero geometry copies
 * HeroSection, so keep the two in step. /ehr adds its content sections under
 * the hero; /rate-check is the hero alone. The pages only register interest,
 * so the header is the logo alone, linking home, and the footer is one row of
 * legal links.
 */

import { useEffect, type ReactNode } from "react";
import { Link } from "react-router";
import { Constellation } from "@/components/landing/Constellation";
import { NAVBAR_HEIGHT } from "@/components/landing/LandingNavbar";
import { S7Logo } from "@/components/landing/S7Logo";
import { InvestorStrip } from "@/components/landing/home/InvestorStrip";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";

/* Literal page ink, like HeroSection, so the first-paint `dark` class cannot
   touch the hero type. */
const SUBHEAD_COLOR = "hsl(225 45% 14% / 0.66)";
/* The site footer's links sit at 0.40 ink, under the 4.5:1 contrast small
   text needs; 0.66 is the lightest ink that clears it on the cream page. */
const FOOTER_INK = "hsl(var(--foreground) / 0.66)";

function Glow({
  left,
  top,
  width,
  height,
  color,
  blur,
}: {
  left: string;
  top: string;
  width: number;
  height: number;
  color: string;
  blur: number;
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        zIndex: 0,
        left,
        top,
        transform: "translate(-50%, -50%)",
        width,
        height,
        borderRadius: "50%",
        pointerEvents: "none",
        background: `radial-gradient(ellipse at center, ${color}, transparent 70%)`,
        filter: `blur(${blur}px)`,
      }}
    />
  );
}

function WaitlistFooter() {
  const openCookiePreferences = useCookieConsentStore((s) => s.openPreferences);
  const linkClass = "text-[14px] no-underline transition-opacity hover:opacity-70";

  function handleCookiePrefs(e: React.MouseEvent) {
    e.preventDefault();
    openCookiePreferences();
  }

  return (
    <footer
      className="px-5 py-8"
      style={{ borderTop: "1px solid hsl(var(--foreground) / 0.06)" }}
    >
      <div
        className="mx-auto flex flex-col-reverse items-center gap-4 text-center md:flex-row md:justify-between md:text-left"
        style={{ maxWidth: 1224 }}
      >
        <p className="text-[13px] m-0" style={{ color: FOOTER_INK }}>
          &copy; 2026 S7 Lab, Inc. All rights reserved.
        </p>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          <Link to="/privacy" className={linkClass} style={{ color: FOOTER_INK }}>
            Privacy policy
          </Link>
          <Link to="/terms" className={linkClass} style={{ color: FOOTER_INK }}>
            Terms of service
          </Link>
          <a href="#" onClick={handleCookiePrefs} className={linkClass} style={{ color: FOOTER_INK }}>
            Cookie preferences
          </a>
          <a href="#" onClick={handleCookiePrefs} className={linkClass} style={{ color: FOOTER_INK }}>
            Do Not Sell or Share My Personal Information
          </a>
        </div>
      </div>
    </footer>
  );
}

interface WaitlistLayoutProps {
  /** Browser tab title; the prerender writes the same one into the HTML. */
  documentTitle: string;
  title: ReactNode;
  subhead: ReactNode;
  /** The form, under the subhead. */
  children: ReactNode;
  /** Content sections under the hero, in the homepage's section styles. */
  sections?: ReactNode;
}

export function WaitlistLayout({
  documentTitle,
  title,
  subhead,
  children,
  sections,
}: WaitlistLayoutProps) {
  // Landing surfaces are always light mode.
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = documentTitle;
    return () => {
      document.title = prev;
    };
  }, [documentTitle]);

  return (
    <div className="min-h-dvh">
      {/* LandingNavbar's logo block, without the menu. */}
      <header
        style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3, height: NAVBAR_HEIGHT }}
      >
        <div className="relative mx-auto h-full" style={{ maxWidth: 1440 }}>
          {/* A full page load: the homepage sets no title of its own, so a
              client-side navigation would keep this page's title and head. */}
          <a
            href="/"
            className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2 sm:gap-3 text-foreground no-underline left-4 sm:left-[88px]"
            aria-label="Altair home"
          >
            <span className="hidden sm:inline-flex">
              <S7Logo size={36} />
            </span>
            <span className="sm:hidden inline-flex">
              <S7Logo size={28} />
            </span>
            <span className="text-[20px] sm:text-[26px] font-semibold tracking-tight">
              Altair
            </span>
          </a>
        </div>
      </header>

      <main>
        <section
          className="home-anim wl-hero"
          style={{
            position: "relative",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "96px 24px 190px",
            overflow: "hidden",
          }}
        >
          <Glow left="14%" top="62%" width={680} height={460} blur={34} color="hsl(var(--accent-violet-glow) / 0.15)" />
          <Glow left="86%" top="28%" width={640} height={440} blur={34} color="hsl(var(--accent-sky-glow) / 0.14)" />
          <Glow left="52%" top="94%" width={900} height={420} blur={38} color="hsl(var(--accent-emerald-glow) / 0.10)" />

          <Constellation
            masked
            clustered
            tints={["violet", "sky"]}
            inkRgb="15,23,42"
            lineAlpha={0.4}
            dotAlpha={0.85}
            clearMask="radial-gradient(ellipse 420px 105px at 50% calc(100% - 138px), transparent 60%, black 100%)"
            style={{ zIndex: 1 }}
          />

          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: 1180,
            }}
          >
            <h1
              className="wl-h1"
              style={{
                margin: 0,
                textAlign: "center",
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: "clamp(38px,5.2vw,68px)",
                fontWeight: 600,
                letterSpacing: "-0.03em",
                lineHeight: 1.08,
                color: "hsl(225 45% 14%)",
                maxWidth: 1040,
                textWrap: "balance",
              }}
            >
              {title}
            </h1>

            <p
              className="wl-subhead"
              style={{
                margin: "36px 0 0",
                textAlign: "center",
                fontFamily: "'Outfit',sans-serif",
                fontSize: "clamp(18px,1.8vw,22px)",
                lineHeight: 1.5,
                color: SUBHEAD_COLOR,
                maxWidth: 640,
                textWrap: "balance",
              }}
            >
              {subhead}
            </p>

            {children}
          </div>

          {/* HeroSection's padding rules, under this page's own class. */}
          <style>{`
            @supports (min-height: 100svh) {
              .wl-hero {
                min-height: 100svh !important;
              }
            }
            @media (max-width: 768px) {
              .wl-hero {
                padding-top: 72px !important;
                padding-bottom: 170px !important;
              }
              .wl-subhead {
                margin-top: 20px !important;
              }
            }
            @media (max-width: 380px) {
              .wl-h1 {
                font-size: 35px !important;
              }
            }
            /* A title line that starts a new line from tablet width up;
               on phones the title wraps on its own. */
            @media (min-width: 769px) {
              .wl-br {
                display: block;
              }
            }
            @media (max-width: 768px) and (prefers-reduced-motion: reduce) {
              .wl-hero {
                padding-bottom: 250px !important;
              }
            }
          `}</style>

          <InvestorStrip />
        </section>

        {sections}
      </main>

      <WaitlistFooter />
    </div>
  );
}
